import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmStore } from '@/shared/utils/confirm-store/confirm-store.util';

import { Button } from '../../ui/Button/Button';
import { Input } from '../../ui/Input/Input';
import { Label } from '../../ui/Label/Label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/Dialog/Dialog';

/**
 * Mounted ONCE, in the shell. The single subscriber to the confirm store.
 *
 * The dialog is not dismissible while its mutation is in flight — `onCancel`
 * and the overlay both defer to the store, which ignores a cancel mid-request.
 * Dismissing then would strand a request whose result nobody reads.
 *
 * The description and the failure notice live in a `DialogBody`, not in the
 * header: `DialogContent` is `overflow-hidden`, so anything outside the body is
 * CLIPPED rather than scrolled once it exceeds the clamp. A destructive confirm
 * is exactly where long copy shows up — "312 members have answered this", a
 * server's refusal reason — and losing the bottom of that text is losing the
 * reason to say no (U0: an overlay contains its own content).
 *
 * The body is rendered only when it has something in it, so the common
 * title-and-buttons confirm keeps its tight two-row shape.
 */
export function ConfirmationRoot() {
  const { t } = useTranslation('uiKit');
  const state = useSyncExternalStore(confirmStore.subscribe, confirmStore.getSnapshot);
  const { options, isPending, error, typed } = state;
  const needsPhrase = options.confirmPhrase !== undefined;
  const isUnlocked = confirmStore.isUnlocked();
  const hasBody = options.description !== undefined || error !== null || needsPhrase;

  return (
    <Dialog
      open={state.open}
      onOpenChange={(next) => {
        if (!next) confirmStore.cancel();
      }}
    >
      <DialogContent size="sm" hideClose={isPending}>
        <DialogHeader>
          <DialogTitle>{options.title ?? t('confirm.title')}</DialogTitle>
        </DialogHeader>

        {!hasBody ? null : (
          <DialogBody className="gap-sm">
            {options.description === undefined ? null : (
              <DialogDescription>{options.description}</DialogDescription>
            )}

            {!needsPhrase ? null : (
              <div className="flex flex-col gap-xs">
                <Label htmlFor="confirm-phrase">
                  {options.confirmPhraseLabel ??
                    t('confirm.typeToConfirm', { phrase: options.confirmPhrase ?? '' })}
                </Label>
                <Input
                  id="confirm-phrase"
                  value={typed}
                  autoComplete="off"
                  disabled={isPending}
                  onChange={(event) => {
                    confirmStore.type(event.target.value);
                  }}
                />
              </div>
            )}

            {error === null ? null : (
              // The dialog stays open on failure and says why, right where the
              // operator is already looking.
              <p role="alert" className="text-caption text-destructive">
                {error === '' ? t('confirm.failed') : error}
              </p>
            )}
          </DialogBody>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => {
              confirmStore.cancel();
            }}
          >
            {options.cancelLabel ?? t('confirm.cancel')}
          </Button>
          <Button
            variant={options.tone === 'danger' ? 'destructive' : 'primary'}
            isLoading={isPending}
            disabled={!isUnlocked}
            onClick={() => {
              void confirmStore.accept();
            }}
          >
            {options.confirmLabel ?? t('confirm.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
